import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Button,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  FiberManualRecord as FiberManualRecordIcon,
} from "@mui/icons-material";
import { getDevices } from "../api/devices";
import TopNavBar from "../components/TopNavBar";
import { useAuth } from "../context/AuthContext";
import useSSESubscription from "../hooks/useSSESubscription";
import DeviceStatusTable from "../components/DeviceStatusTable";
import {
  compareDevicesForDashboard,
  getRobotStatus,
  getRobotStatusCategory,
} from "../components/deviceStatusUtils";

export default function HomePage() {
  const { user } = useAuth();
  const username = user?.username;

  const [devices, setDevices] = useState([]);
  const [rcDevices, setRcDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState([]);
  const [error, setError] = useState(null);
  const [lastEventTime, setLastEventTime] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Filter states with local storage persistence
  const [manufacturerFilter, setManufacturerFilter] = useState(() => {
    return localStorage.getItem("deviceFilters_manufacturer") || "all";
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem("deviceFilters_status") || "all";
  });
  const [customerFilter, setCustomerFilter] = useState(() => {
    return localStorage.getItem("deviceFilters_customer") || "all";
  });
  const [favoriteDeviceMap, setFavoriteDeviceMap] = useState(() => {
    try {
      const raw = localStorage.getItem("deviceFavorites_map");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Use ref to store fetchDevices function to avoid dependency cycle
  const fetchDevicesRef = useRef();

  // SSE message handler for dashboard
  const handleSSEMessage = useCallback(
    (message, conflict) => {
      console.log("Dashboard received SSE message:", message);

      // Defer state update to avoid setState during render warning
      setTimeout(() => {
        setLastEventTime(new Date());
      }, 0);

      // Handle different types of updates relevant to dashboard
      switch (message.type) {
        case "robot_status_updated":
          const deviceId = message.entityId;
          console.log("Full message structure:", message);
          console.log("Device ID:", deviceId);
          console.log("Message updateData:", message.updateData);
          console.log("Message data:", message.data);

          // Check both possible data locations
          const latestLog =
            message.updateData?.latestLog ||
            message.data?.latestLog ||
            message.latestLog;
          const deviceData =
            message.updateData?.device ||
            message.data?.device ||
            message.device;

          console.log("Extracted latestLog:", latestLog);
          console.log("Extracted deviceData:", deviceData);

          if (deviceId && latestLog) {
            // Extract only dashboard-relevant fields
            const dashboardUpdates = {
              battery_level: latestLog.battery_level,
              operating_state: latestLog.operating_state,
              movement_state: latestLog.movement_state,
              cleaning_step: latestLog.cleaning_step,
            };

            setDevices((prevDevices) =>
              prevDevices.map((device) =>
                device.uk_device_id === deviceId
                  ? {
                      ...device,
                      ...dashboardUpdates,
                      // Also update connected status and last_update
                      connected: deviceData?.connected ?? device.connected,
                      last_update:
                        deviceData?.last_update ?? device.last_update,
                    }
                  : device,
              ),
            );

            setLastUpdated(new Date().toLocaleString());
            console.log(
              `🔄 Updated device ${deviceId} with real-time data`,
              dashboardUpdates,
            );
          } else {
            console.warn(
              "robot_status_updated message missing deviceId or latestLog",
              { deviceId, latestLog, fullMessage: message },
            );
          }
          break;
        case "device_updated":
          console.log(
            "Legacy device_updated message received - consider updating to robot_status_updated",
          );
          // Fallback for legacy messages
          fetchDevicesRef.current?.();
          break;
        case "bulk_device_updates":
          console.log(
            `Bulk device updates received (${message.updateData?.count || 0} devices), refreshing...`,
          );
          fetchDevicesRef.current?.();
          break;
        case "customer_updated":
          console.log("Customer updated, may affect devices...");
          // Optionally refresh devices if customer changes affect device display
          fetchDevicesRef.current?.();
          break;
        case "heartbeat":
          // Ignore heartbeat messages for dashboard
          break;
        default:
          console.log(`Dashboard: Unhandled SSE message type: ${message.type}`);
      }
    },
    [], // fetchDevices is stable (empty deps) so we don't need it in dependencies
  );

  // Subscribe to global SSE using our custom hook
  const { isConnected, error: sseError } = useSSESubscription(
    "dashboard_page",
    handleSSEMessage,
    [], // No dependencies since we handle all relevant message types
  );

  // Fetch devices with useCallback to avoid recreating the function on each render
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDevices(true);
      setDevices(data?.devices || []);
      setRcDevices(data?.rc_devices || []);
      console.log("Device data:", data);
      setLastUpdated(new Date().toLocaleString());
    } catch (err) {
      console.error("Error loading devices:", err.message);
      setError("Failed to load devices");
    } finally {
      setLoading(false);
    }
  }, []);

  // Store fetchDevices in ref so SSE handler can access it
  useEffect(() => {
    fetchDevicesRef.current = fetchDevices;
  }, [fetchDevices]);

  // Only run once on mount
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Persist filter states to local storage
  useEffect(() => {
    localStorage.setItem("deviceFilters_manufacturer", manufacturerFilter);
  }, [manufacturerFilter]);

  useEffect(() => {
    localStorage.setItem("deviceFilters_status", statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    localStorage.setItem("deviceFilters_customer", customerFilter);
  }, [customerFilter]);

  useEffect(() => {
    localStorage.setItem(
      "deviceFavorites_map",
      JSON.stringify(favoriteDeviceMap),
    );
  }, [favoriteDeviceMap]);

  // Memoize filtered devices to avoid recalculation on every render
  const activeDevices = useMemo(() => {
    let filtered = devices.filter((device) => device.active === 1);

    // Apply manufacturer filter
    if (manufacturerFilter !== "all") {
      filtered = filtered.filter(
        (device) => device.manufacturer === manufacturerFilter,
      );
    }

    // Apply overall status filter (Good/Warning/Alert)
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (device) => getRobotStatusCategory(device) === statusFilter,
      );
    }

    // Apply customer filter
    if (customerFilter !== "all") {
      if (customerFilter === "unassigned") {
        filtered = filtered.filter(
          (device) => !device.customer_name || device.customer_name === null,
        );
      } else {
        filtered = filtered.filter(
          (device) => (device.customer_name || "") === customerFilter,
        );
      }
    }

    return filtered;
  }, [devices, manufacturerFilter, statusFilter, customerFilter]);

  const sortedDevices = useMemo(() => {
    return [...activeDevices].sort((leftDevice, rightDevice) => {
      const leftFavorite = Boolean(favoriteDeviceMap[leftDevice.rc_device_id]);
      const rightFavorite = Boolean(
        favoriteDeviceMap[rightDevice.rc_device_id],
      );

      if (leftFavorite !== rightFavorite) {
        return leftFavorite ? -1 : 1;
      }

      return compareDevicesForDashboard(leftDevice, rightDevice);
    });
  }, [activeDevices, favoriteDeviceMap]);

  // Get unique manufacturers for filter dropdown
  const availableManufacturers = useMemo(() => {
    const manufacturers = [
      ...new Set(
        devices
          .filter((d) => d.active === 1)
          .map((device) => device.manufacturer),
      ),
    ];
    return manufacturers.filter(Boolean).sort();
  }, [devices]);

  const availableCustomers = useMemo(() => {
    const customers = [
      ...new Set(
        devices
          .filter((device) => device.active === 1)
          .map((device) => device.customer_name)
          .filter(Boolean),
      ),
    ];

    return customers.sort((leftName, rightName) =>
      leftName.localeCompare(rightName),
    );
  }, [devices]);

  const overallStatusCounts = useMemo(() => {
    const totals = {
      alert: 0,
      warning: 0,
      good: 0,
      stale: 0,
      total: 0,
    };

    const activeOnly = devices.filter((device) => device.active === 1);
    totals.total = activeOnly.length;

    activeOnly.forEach((device) => {
      const status = getRobotStatus(device);

      if (status.isStale) {
        totals.stale += 1;
      } else if (status.severity === 0) {
        totals.alert += 1;
      } else if (status.severity === 1) {
        totals.warning += 1;
      } else {
        totals.good += 1;
      }
    });

    return totals;
  }, [devices]);

  const toggleFavoriteDevice = useCallback((deviceId) => {
    if (!deviceId) return;

    setFavoriteDeviceMap((previousMap) => {
      const nextMap = { ...previousMap };

      if (nextMap[deviceId]) {
        delete nextMap[deviceId];
      } else {
        nextMap[deviceId] = true;
      }

      return nextMap;
    });
  }, []);

  const routerDevices = useMemo(
    () =>
      rcDevices.filter(
        (device) => device.active === 1 && device.device_category === "router",
      ),
    [rcDevices],
  );

  // Clear all filters function
  const clearAllFilters = () => {
    setManufacturerFilter("all");
    setStatusFilter("all");
    setCustomerFilter("all");
  };

  return (
    <>
      <TopNavBar
        title="Robot Cleaner Dashboard"
        username={username}
        heading={"Dashboard"}
      />
      <Container sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Hello, {username}
        </Typography>

        {/* Connection Status */}
        <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
          <Chip
            label={isConnected ? "Live Updates" : "Disconnected"}
            color={isConnected ? "success" : "error"}
            size="small"
            icon={isConnected ? <CheckCircleIcon /> : <WarningIcon />}
          />
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastUpdated}
          </Typography>
          {lastEventTime && (
            <Typography variant="caption" color="text.secondary">
              • Last event: {lastEventTime.toLocaleTimeString()}
            </Typography>
          )}
        </Box>

        {/* Filter Controls */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6">Filters</Typography>
            <Typography variant="body2" color="text.secondary">
              ({activeDevices.length} of{" "}
              {devices.filter((d) => d.active === 1).length} devices)
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <FiberManualRecordIcon
                  sx={{ fontSize: 11, color: "#f44336" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {overallStatusCounts.alert}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <FiberManualRecordIcon
                  sx={{ fontSize: 11, color: "#ff9800" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {overallStatusCounts.warning}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <FiberManualRecordIcon
                  sx={{ fontSize: 11, color: "#4caf50" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {overallStatusCounts.good}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <FiberManualRecordIcon
                  sx={{ fontSize: 11, color: "#9e9e9e" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {overallStatusCounts.stale}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                / {overallStatusCounts.total}
              </Typography>
            </Box>
            <Box sx={{ ml: "auto" }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                sx={{ textTransform: "none" }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {/* Manufacturer Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Manufacturer</InputLabel>
              <Select
                value={manufacturerFilter}
                label="Manufacturer"
                onChange={(e) => setManufacturerFilter(e.target.value)}
              >
                <MenuItem value="all">All Manufacturers</MenuItem>
                {availableManufacturers.map((manufacturer) => (
                  <MenuItem key={manufacturer} value={manufacturer}>
                    {manufacturer.charAt(0).toUpperCase() +
                      manufacturer.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Online Status Filter */}
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="good">Good</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="alert">Alert</MenuItem>
              </Select>
            </FormControl>

            {/* Customer Filter */}
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Customer</InputLabel>
              <Select
                value={customerFilter}
                label="Customer"
                onChange={(e) => setCustomerFilter(e.target.value)}
              >
                <MenuItem value="all">All Customers</MenuItem>
                {availableCustomers.map((customerName) => (
                  <MenuItem key={customerName} value={customerName}>
                    {customerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading devices...</Typography>
          </Box>
        )}
        {error && <Typography color="error">{error}</Typography>}

        <Tabs
          value={activeTab}
          onChange={(_, nextTab) => setActiveTab(nextTab)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Robots" sx={{ textTransform: "none" }} />
          <Tab label="Routers" sx={{ textTransform: "none" }} />
        </Tabs>

        {activeTab === 0 && (
          <>
            {!loading && !error && activeDevices.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No active devices found
                </Typography>
              </Box>
            )}

            {!loading && !error && activeDevices.length > 0 && (
              <DeviceStatusTable
                devices={sortedDevices}
                favoritesMap={favoriteDeviceMap}
                onToggleFavorite={toggleFavoriteDevice}
              />
            )}
          </>
        )}

        {activeTab === 1 && (
          <>
            {!loading && !error && routerDevices.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No routers found
                </Typography>
              </Box>
            )}

            {!loading && !error && routerDevices.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Customer
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Model</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Manufacturer
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Serial Number
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        MAC Address
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Firmware Version
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Software Version
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Connection
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Connected
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Last Seen
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {routerDevices.map((device) => (
                      <TableRow key={device.pk_id}>
                        <TableCell>{device.name || "–"}</TableCell>
                        <TableCell>{device.customer_name || "–"}</TableCell>
                        <TableCell>{device.model || "–"}</TableCell>
                        <TableCell>{device.manufacturer || "–"}</TableCell>
                        <TableCell>{device.serial_number || "–"}</TableCell>
                        <TableCell>{device.mac_address || "–"}</TableCell>
                        <TableCell>{device.firmware_version || "–"}</TableCell>
                        <TableCell>{device.software_version || "–"}</TableCell>
                        <TableCell>{device.connection_type || "–"}</TableCell>
                        <TableCell>
                          {Number(device.connected) === 1 ? "Yes" : "No"}
                        </TableCell>
                        <TableCell>
                          {device.last_seen_at
                            ? new Date(device.last_seen_at).toLocaleString()
                            : "–"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Container>
    </>
  );
}
